interface Args {
	deviceId: string | undefined | null;
	userToken: string | undefined | null;
	userIp?: string | string[];
	browserLanguage?: string;
	path?: string | null;
	cookies?: Record<string, string>;
	userAgent?: string;
	platform?: 'www' | 'msft-teams';
	noCache?: boolean;
	customHeaders?: Record<string, string | undefined>;
	excludedHeaders?: string[];
}

const Platform = {
	WWW: 'www',
} as const;

export const getPlatform = (): 'www' => Platform.WWW;

function createReqHeaders({
	deviceId = '',
	userToken,
	userIp,
	path,
	userAgent,
	cookies,
	noCache,
	browserLanguage = '',
	customHeaders = {},
	excludedHeaders = [],
}: Args): { [key: string]: string } {
	const userIpHeader = Array.isArray(userIp) ? userIp.join(',') : userIp;
	// For Server-side Rendered pages, it's difficult to determine what Platform
	// we're using unless we explicitly pass it in. So, we should take the `platform`
	// value if provided. Otherwise, attempt to derive it b/c we're likely on a
	// Client-Side Page

	const cookiesString = cookies
		? Object.entries(cookies)
				.map(([key, value]) => `${key}=${value}`)
				.join('; ')
		: null;

	const reqHeaders: Record<string, string> = {
		'x-device-id': deviceId || '',
		'x-device-version': process.env.BUILD_ID || 'unknown',
		'x-device-platform': 'www',
		'x-client-timezone': Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Los_Angeles',
		...(noCache
			? {
					'Cache-Control': 'no-cache',
					Pragma: 'no-cache',
					Expires: '0',
				}
			: {}),
		...(userIp ? { 'X-Forwarded-For': userIpHeader } : {}),
		...(userToken ? { 'x-session-token': userToken } : {}),
		...(browserLanguage ? { 'Accept-Language': browserLanguage } : {}),
		...(path ? { 'x-www-req-from': path } : {}),
		...(cookiesString ? { Cookie: cookiesString } : {}),
		...customHeaders,
	};

	if (typeof window === 'undefined') {
		reqHeaders['user-agent'] = userAgent ?? 'Calm-API-Client/1.0';
	}

	excludedHeaders.forEach(header => {
		if (header in reqHeaders) {
			delete reqHeaders[header as keyof typeof reqHeaders];
		}
	});

	return reqHeaders;
}

export default createReqHeaders;
